<template>
    <div class="tw:px-6 tw:py-6">
        <!-- Error state -->
        <div v-if="error" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
            <Button variant="danger" @click="refresh">
                {{ $t('back.dashboard.retry') }}
            </Button>
        </div>

        <!-- Loading skeleton -->
        <template v-if="loading">
            <div class="tw:grid tw:grid-cols-3 tw:gap-4 tw:mb-6">
                <div v-for="i in 3" :key="i" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <div class="tw:h-16 tw:bg-gray-100 tw:animate-pulse tw:rounded"></div>
                </div>
            </div>
            <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                <div v-for="i in 4" :key="i" class="tw:h-8 tw:bg-gray-100 tw:animate-pulse tw:rounded tw:mb-2"></div>
            </div>
        </template>

        <!-- Data -->
        <template v-else-if="data">
            <div class="tw:flex tw:items-center tw:gap-2 tw:mb-1">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900">{{ $t('back.dashboard.activity.section_title') }}</h3>
                <Badge v-if="isMultiSeason && activityComparison" variant="info" :label="$t('back.dashboard.comparison')" />
            </div>
            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.activity.section_description') }}</p>

            <!-- Summary KPIs: single season -->
            <div v-if="summaryKpis && !isMultiSeason" class="tw:grid tw:grid-cols-3 tw:gap-4 tw:mb-6">
                <StatsCard
                    :title="$t('back.dashboard.activity.kpi_events')"
                    :value="formatNumber(summaryKpis.total_events)"
                    variant="primary"
                />
                <StatsCard
                    :title="$t('back.dashboard.activity.kpi_revenue')"
                    :value="formatCurrency(summaryKpis.total_revenue)"
                    variant="success"
                />
                <StatsCard
                    :title="$t('back.dashboard.activity.kpi_attendance')"
                    :value="formatNumber(summaryKpis.total_attendance)"
                    variant="warning"
                />
            </div>

            <!-- Multi-season: comparison table -->
            <div v-if="isMultiSeason && activityComparison" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-6">
                <table class="tw:w-full tw:text-sm">
                    <thead>
                        <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                            <th class="tw:pb-2">Métrica</th>
                            <th class="tw:pb-2 tw:text-right" v-for="row in activityComparison" :key="row.season">{{ row.season }}</th>
                            <th class="tw:pb-2 tw:text-right" v-if="activityDelta">{{ $t('back.dashboard.delta') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="tw:border-t tw:border-gray-100">
                            <td class="tw:py-1.5">{{ $t('back.dashboard.activity.kpi_events') }}</td>
                            <td v-for="row in activityComparison" :key="'e' + row.season" class="tw:py-1.5 tw:text-right">{{ formatNumber(row.totalEvents) }}</td>
                            <td v-if="activityDelta" class="tw:py-1.5 tw:text-right" :class="deltaClass(activityDelta.totalEvents)">{{ formatDelta(activityDelta.totalEvents, 'num') }}</td>
                        </tr>
                        <tr class="tw:border-t tw:border-gray-100">
                            <td class="tw:py-1.5">{{ $t('back.dashboard.activity.kpi_revenue') }}</td>
                            <td v-for="row in activityComparison" :key="'r' + row.season" class="tw:py-1.5 tw:text-right">{{ formatCurrency(row.totalRevenue) }}</td>
                            <td v-if="activityDelta" class="tw:py-1.5 tw:text-right" :class="deltaClass(activityDelta.totalRevenue)">{{ formatDeltaCurrency(activityDelta.totalRevenue) }}</td>
                        </tr>
                        <tr class="tw:border-t tw:border-gray-100">
                            <td class="tw:py-1.5">{{ $t('back.dashboard.activity.kpi_attendance') }}</td>
                            <td v-for="row in activityComparison" :key="'a' + row.season" class="tw:py-1.5 tw:text-right">{{ formatNumber(row.totalAttendance) }}</td>
                            <td v-if="activityDelta" class="tw:py-1.5 tw:text-right" :class="deltaClass(activityDelta.totalAttendance)">{{ formatDelta(activityDelta.totalAttendance, 'num') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Extra income breakdown by category -->
            <div v-if="extraIncomeBreakdown.length" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-6">
                <h3 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.activity.breakdown_title') }}</h3>
                <table class="tw:w-full tw:text-sm">
                    <thead>
                        <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                            <th class="tw:pb-2">{{ $t('back.dashboard.activity.breakdown_category') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.activity.breakdown_revenue') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.activity.breakdown_events') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.activity.breakdown_attendance') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="cat in extraIncomeBreakdown" :key="cat.key" class="tw:border-t tw:border-gray-100">
                            <td class="tw:py-1.5 tw:capitalize">{{ $t(`back.dashboard.activity.cat_${cat.key}`) }}</td>
                            <td class="tw:py-1.5 tw:text-right tw:font-medium">{{ formatCurrency(cat.revenue) }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ cat.events }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ formatNumber(cat.attendance) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr class="tw:border-t tw:border-gray-300 tw:font-semibold">
                            <td class="tw:py-2">{{ $t('back.dashboard.total') }}</td>
                            <td class="tw:py-2 tw:text-right">{{ formatCurrency(totalExtraIncome) }}</td>
                            <td class="tw:py-2 tw:text-right">{{ totalExtraEvents }}</td>
                            <td class="tw:py-2 tw:text-right">{{ formatNumber(totalExtraAttendance) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <!-- Events table (flat, no per-season grouping) -->
            <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:overflow-hidden tw:bg-white">
                <div class="tw:overflow-x-auto">
                    <table class="tw:min-w-full tw:divide-y tw:divide-gray-200">
                        <thead class="tw:bg-gray-50">
                            <tr>
                                <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-left">
                                    {{ $t('back.dashboard.matches.col_event') }}
                                </th>
                                <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-left">
                                    {{ $t('back.dashboard.matches.col_date') }}
                                </th>
                                <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                    {{ $t('back.dashboard.matches.col_revenue') }}
                                </th>
                                <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                    {{ $t('back.dashboard.matches.col_attendance') }}
                                </th>
                                <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                    {{ $t('back.dashboard.matches.col_online') }}
                                </th>
                                <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                    {{ $t('back.dashboard.matches.col_taquilla') }}
                                </th>
                                <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                    {{ $t('back.dashboard.matches.col_invitations') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody class="tw:divide-y tw:divide-gray-200">
                            <tr v-for="event in eventsTable" :key="event.event_id" class="hover:tw:bg-gray-50">
                                <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-900 tw:font-medium">{{ event.event_name }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500">{{ formatDate(event.event_date) }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-700 tw:text-right tw:font-medium">{{ formatCurrency(event.total_revenue) }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-700 tw:text-right">{{ formatNumber(event.total_attendance) }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(event.online_tickets) }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(event.taquilla_tickets) }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(event.invitations) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>

        <!-- Empty state -->
        <div v-else class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-8 tw:text-center">
            <p class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.activity.empty') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { toRef, computed } from 'vue';
    import { useActivity } from '@/composables/admin/dashboard/useActivity';
    import { formatCurrency, formatNumber, formatDate } from '@/utils/format';
    import StatsCard from '@/modules/admin/ui/StatsCard.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import Badge from '@/modules/admin/ui/Badge.vue';

    const props = defineProps({
        seasonIds: { type: Array, required: true },
        seasons: { type: Array, required: true },
    });


    const seasonIdsRef = toRef(props, 'seasonIds');
    const seasonsRef = toRef(props, 'seasons');

    const {
        data,
        loading,
        error,
        isMultiSeason,
        summaryKpis,
        extraIncomeBreakdown,
        eventsTable,
        activityComparison,
        activityDelta,
        seasonBreakdown,
        refresh,
    } = useActivity(seasonIdsRef, seasonsRef);

    const totalExtraIncome = computed(() =>
        extraIncomeBreakdown.value.reduce((s, c) => s + c.revenue, 0),
    );
    const totalExtraEvents = computed(() =>
        extraIncomeBreakdown.value.reduce((s, c) => s + c.events, 0),
    );
    const totalExtraAttendance = computed(() =>
        extraIncomeBreakdown.value.reduce((s, c) => s + c.attendance, 0),
    );

    /** Clase de color según el valor del delta */
    const deltaClass = value => {
        if (value === 0 || value === null || value === undefined) return 'tw:text-gray-500';
        return value > 0
            ? 'tw:text-green-600 tw:font-semibold'
            : 'tw:text-red-600 tw:font-semibold';
    };

    /** Formatea delta numérico con signo */
    const formatDelta = (value, unit) => {
        if (value === null || value === undefined) return '-';
        const prefix = value >= 0 ? '+' : '';
        const abs = Math.abs(value);
        const formatted = Number.isInteger(value)
            ? abs.toLocaleString('es-ES')
            : abs.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        return `${prefix}${formatted}`;
    };

    /** Formatea delta de moneda con signo */
    const formatDeltaCurrency = value => {
        if (value === null || value === undefined) return '-';
        const prefix = value >= 0 ? '+' : '';
        const abs = Math.abs(value);
        return `${prefix}${abs.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
    };
</script>
